import { $generateHtmlFromNodes } from "@lexical/html";
import { $isLinkNode } from "@lexical/link";
import {
  $createTextNode,
  $getRoot,
  $insertNodes,
  $isElementNode,
  $isTextNode,
  type LexicalNode,
} from "lexical";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import type { ChannelName } from "../../../../channels/types";
import { $isBroadcastNode } from "../../../../message/editor/nodes/broadcast-node";
import { $isChannelNode } from "../../../../message/editor/nodes/channel/channel-node";
import { $isVideoNode } from "../../../../message/editor/nodes/video-node";
import type { SerialisedLexicalEditorDesign } from "../../../../message/editor/types/serialised-editor-design";
import { generateNodesFromHtml } from "../../../../message/editor/utils/generate-nodes-from-html";
import { createConfiguredEditor } from "../../../../message/editor/utils/headless-editor";
import type { ChatChannelRecipient } from "../../../../message/editor/utils/use-chat-channel-recipients";

import { $isDynamicContentNode } from "../../../../message/editor/nodes/dynamic-content/dynamic-content-node";
import { $isDynamicContentToolbarNode } from "../../../../message/editor/nodes/dynamic-content/helpers";

function unsupportedVideoNode(node: LexicalNode, slackVideoEnabled: boolean): boolean {
  // Cannot send uploaded videos unless the Slack app has been updated
  return $isVideoNode(node) && !slackVideoEnabled;
}

/**
 * Treat dynamic containers as "empty" if they have no meaningful content.
 */
function dynamicContainerHasMeaningfulChildren(node: LexicalNode): boolean {
  if (!$isDynamicContentNode(node)) return true;

  const kids = node.getChildren();

  // Ignore toolbar nodes
  const meaningful = kids.filter((k) => !$isDynamicContentToolbarNode(k));

  // Ignore whitespace-only text nodes
  const meaningfulNonWhitespace = meaningful.filter((k) => {
    if ($isTextNode(k)) return k.getTextContent().trim().length > 0;
    return true;
  });

  return meaningfulNonWhitespace.length > 0;
}

export function convertLexicalDesign(
  from: NonNullable<ChannelName>,
  to: NonNullable<ChannelName>,
  design: SerialisedLexicalEditorDesign | null,
  slackVideoEnabled: boolean = false,
  slackChannelRecipients: ChatChannelRecipient[] = [],
  blackListedNodeMatchers: ((node: LexicalNode) => boolean)[] = [],
): SerialisedLexicalEditorDesign {
  const oldEditor = createConfiguredEditor({ format: channelToFormat(from) });

  const state = oldEditor.parseEditorState(JSON.stringify(design));
  oldEditor.setEditorState(state);

  let htmlString = "";

  oldEditor.update(
    () => {
      const stripIllegalNodes = (node: LexicalNode): LexicalNode | null => {
        // 1. Check against custom blacklisted matchers
        if (blackListedNodeMatchers.some((matcher) => matcher(node))) {
          node.remove();
          return null;
        }

        // 2. Existing transformation logic
        if (to === "twilio" && $isLinkNode(node)) {
          return $createTextNode(node.getURL());
        }

        if (from === "slack" && $isBroadcastNode(node)) {
          return $createTextNode(node.getDisplay());
        }

        if (from === "slack" && $isChannelNode(node)) {
          if (slackChannelRecipients.length === 0) {
            return $createTextNode(`#${node.getChannelId()}`);
          }

          const channelName = slackChannelRecipients.find(
            ({ channel_id }) => node.getChannelId() === channel_id,
          )?.display;

          const display = channelName ? `#${channelName}` : "⚠️ Unknown Channel";
          return $createTextNode(display);
        }

        // 3. Recursive step for ElementNodes
        if ($isElementNode(node)) {
          const childNodes = node.getChildren();
          // Filter out nulls returned by blacklisted removals
          const preparedChildNodes = childNodes
            .map(stripIllegalNodes)
            .filter((n): n is LexicalNode => n !== null);

          node.clear(); // Cleaner than manual remove loop
          node.append(...preparedChildNodes);

          if ($isDynamicContentNode(node) && !dynamicContainerHasMeaningfulChildren(node)) {
            node.remove();
            return null;
          }
        }

        return node;
      };

      const topLevelNodes = $getRoot().getChildren();
      const processedNodes = topLevelNodes
        .map(stripIllegalNodes)
        .filter((n): n is LexicalNode => n !== null);

      $getRoot().clear();
      $getRoot().append(...processedNodes);

      htmlString = $generateHtmlFromNodes(oldEditor, null);
    },
    { discrete: true },
  );

  const newEditor = createConfiguredEditor({ format: channelToFormat(to) });

  newEditor.update(
    () => {
      const nodes = generateNodesFromHtml(newEditor, htmlString, to);

      // Filter final nodes against video constraints AND the blacklist AND empty dynamic containers
      const filteredNodes = nodes.filter((node) => {
        const isUnsupportedVideo = to === "slack" && unsupportedVideoNode(node, slackVideoEnabled);
        if (isUnsupportedVideo) return false;

        const isBlacklisted = blackListedNodeMatchers.some((m) => m(node));
        if (isBlacklisted) return false;

        // Only affects top-level nodes; the recursive prune above is the main protection.
        if ($isDynamicContentNode(node) && !dynamicContainerHasMeaningfulChildren(node))
          return false;

        return true;
      });

      $insertNodes(filteredNodes);

      const rootKids = $getRoot().getChildren();
      for (const kid of rootKids) {
        if ($isDynamicContentNode(kid) && !dynamicContainerHasMeaningfulChildren(kid)) {
          kid.remove();
        }
      }
    },
    { discrete: true },
  );

  return newEditor.getEditorState().toJSON();
}
